import React, { useState } from "react";
import { DataTable, DataTableFilterMeta } from "primereact/datatable";
import { Column } from "primereact/column";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Users, UserUpdateBody } from "../api/models/user.models";
import { FilterMatchMode } from "primereact/api";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import useUsers from "../hooks/useUsers";
import { Button } from "primereact/button";
import { Menu } from "primereact/menu";
import { OverlayPanel } from "primereact/overlaypanel";
import { HttpRequestStatus, LoadingContainer } from "./loadingContainer";
import CreateUserDialog from "../dialogs/createUserDialog";
import { MenuItemCommandEvent } from "primereact/menuitem";
import EditUserDialog from "../dialogs/editUserDialog";
import EditUserRolesDialog from "../dialogs/editUserRolesDialog";
import { Company } from "../api/models/companies.models";
import { useStore } from "zustand";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { confirmDialog } from "primereact/confirmdialog";

const StyledTable = styled(DataTable)`
  & .edit-product-link {
    color: var(--sumi-blue);
    cursor: pointer;

    &:hover {
      color: var(--sumi-light-blue);
      text-decoration: underline;
    }
  }

  .p-datatable-tbody > tr > td {
    border: none;
    background-color: #fdfdfd;
    border-bottom: 1px solid #e1e1e1;
    padding: 0.5rem 1rem;
  }

  .p-datatable-thead > tr > th {
    border: none;
    font-weight: 500;
    font-size: 0.8rem;
    color: #888888;
    text-transform: uppercase;
  }

  // rotate svg icon
  & .p-row-toggler svg {
    color: var(--text-color);
    transform: rotate(90deg);
  }

  // rotate 180 degrees when attribute expanded is true
  & .p-row-toggler[aria-expanded="true"] svg {
    transform: rotate(180deg);
  }

  .p-datatable-row-expansion {
    > td {
      padding: 0;
      border: none;
    }

    & .p-datatable-tbody > tr > td {
      background-color: var(--sumi-background-blue);
    }

    & .p-datatable-tbody > tr > td {
      border: none;
    }
  }
`;

const CompanyName = styled.h4`
  margin: 0;
  text-align: left;
  margin-bottom: 0;
  margin-top: 2rem;
`;

interface UserTableProps {
  users: Users[];
  usersIsLoading: boolean;
  deleteUser: (userId: number) => void;
  updateUser: (data: UserUpdateBody) => Promise<any>;
  isUpdatingUser: boolean;
  isDeletingUser: boolean;
  handleAddUserClick: () => void;
  companies: Company[];
  canManageRoles: boolean;
}

const UserTable: React.FC<UserTableProps> = ({
  users,
  usersIsLoading,
  deleteUser,
  updateUser,
  isUpdatingUser,
  isDeletingUser,
  handleAddUserClick,
  companies,
  canManageRoles,
}) => {
  const { t } = useTranslation();
  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");
  const [showEditUserDialog, setShowEditUserDialog] = useState(false);
  const [showEditUserRolesDialog, setShowEditUserRolesDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Users | undefined>(
    undefined
  );

  const [companyId, companyName, organizationId, organizationName] = useUIStore(
    useShallow((state: UIState) => [
      state.companyId,
      state.companyName,
      state.organizationId,
      state.organizationName,
    ])
  );

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    "names.firstName": { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    "names.lastName": { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    "email.email": { value: null, matchMode: FilterMatchMode.STARTS_WITH },
    "phoneNumber.number": {
      value: null,
      matchMode: FilterMatchMode.STARTS_WITH,
    },
  });

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let _filters = { ...filters };
    // @ts-ignore
    _filters["global"].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const renderHeader = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        marginBottom: "1rem",
      }}
    >
      <IconField iconPosition="left">
        <InputIcon className="pi pi-search" />
        <InputText
          value={globalFilterValue}
          onChange={onGlobalFilterChange}
          placeholder={t("label-search")}
        />
      </IconField>
    </div>
  );

  const header = renderHeader();

  const getMenuItems = (user: Users) => {
    return [
      {
        label: t("cmd-edit-user"),
        icon: "pi pi-pencil",
        command: (e: MenuItemCommandEvent) => {
          opRefs.current[user.id]?.hide();
          setShowEditUserDialog(true);
          setSelectedUser(user);
        },
      },
      {
        label: t("cmd-edit-user-roles"),
        icon: "pi pi-users",
        command: () => {
          opRefs.current[user.id]?.hide();
          setShowEditUserRolesDialog(true);
          setSelectedUser(user);
        },
      },
      {
        label: t("cmd-delete-user"),
        icon: "pi pi-trash",
        command: () => {
          opRefs.current[user.id]?.hide();
          confirmDialog({
            showHeader: false,
            message: t("are-you-sure-you-want-remove-user", {
              name: user.names.firstName + " " + user.names.lastName,
            }),
            acceptLabel: t("cmd-delete"),
            rejectLabel: t("cmd-cancel"),
            icon: "pi pi-exclamation-triangle",
            headerClassName: "p-confirm-dialog-header-danger",
            acceptClassName: "p-button-danger",
            accept: () => {
              deleteUser(user.id);
            },
            style: { minWidth: "300px" },
          });
        },
      },
    ];
  };

  const opRefs = React.useRef<{ [key: number]: OverlayPanel | null }>({});

  const menuColumnTemplate = (rowData: Users) => {
    return (
      <>
        <Button
          icon="pi pi-ellipsis-v"
          rounded
          text
          aria-label="More"
          onClick={(e) => {
            if (!opRefs.current[rowData.id]) return;
            opRefs.current[rowData.id]?.toggle(e);
          }}
        />
        <OverlayPanel ref={(el) => (opRefs.current[rowData.id] = el)}>
          <Menu model={getMenuItems(rowData)} />
        </OverlayPanel>
      </>
    );
  };

  // Compose table data with extra row for add new user
  // Add a dummy row for the "add new user" button, cast as unknown to Users to avoid TS error
  const tableData = Array.isArray(users)
    ? [...users, { isAddNewRow: true } as unknown as Users]
    : users;

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <LoadingContainer
        status={
          usersIsLoading ? HttpRequestStatus.LOADING : HttpRequestStatus.SUCCESS
        }
      >
        {showEditUserDialog && selectedUser && (
          <EditUserDialog
            show={showEditUserDialog}
            onHide={() => setShowEditUserDialog(false)}
            updateUser={updateUser}
            updatingUser={isUpdatingUser}
            user={selectedUser}
          />
        )}
        {showEditUserRolesDialog && selectedUser && (
          <EditUserRolesDialog
            show={showEditUserRolesDialog}
            onHide={() => setShowEditUserRolesDialog(false)}
            user={selectedUser}
            companies={companies || []}
            companyId={companyId as number} // company id given from context in user view
            organizationId={organizationId as number} // organization id given from context in user view
          />
        )}

        <StyledTable
          value={tableData}
          filters={filters}
          filterDisplay="row"
          globalFilterFields={[
            "names.firstName",
            "names.lastName",
            "email.email",
            "phoneNumber.number",
          ]}
          header={header}
          emptyMessage={t("label-no-users")}
          rowClassName={(rowData) =>
            (rowData as any).isAddNewRow ? "add-new-user-row" : ""
          }
        >
          <Column
            body={(rowData) => {
              if ((rowData as any).isAddNewRow && canManageRoles) {
                return (
                  <div style={{ width: "100%", textAlign: "center" }}>
                    <Button
                      icon="pi pi-user-plus"
                      label={
                        organizationName
                          ? t("label-add-new-user-to-organization", {
                              organizationName: organizationName,
                            })
                          : t("label-add-new-user-to-company", {
                              companyName: companyName,
                            })
                      }
                      onClick={handleAddUserClick}
                      className="p-button-text"
                      style={{
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                        fontSize: "0.9rem",
                      }}
                    />
                  </div>
                );
              }
              return rowData.names?.firstName;
            }}
            header={t("label-first-name")}
          />
          <Column
            body={(rowData) =>
              (rowData as any).isAddNewRow ? null : rowData.names?.lastName
            }
            header={t("label-last-name")}
          />
          <Column
            body={(rowData) =>
              (rowData as any).isAddNewRow ? null : rowData.email?.email
            }
            header={t("label-email")}
          />
          <Column
            body={(rowData) =>
              (rowData as any).isAddNewRow ? null : rowData.phoneNumber?.number
            }
            header={t("label-phone-number")}
          />
          <Column
            body={(rowData) =>
              (rowData as any).isAddNewRow ? null : menuColumnTemplate(rowData)
            }
            style={{ width: "3rem", textAlign: "center" }}
          />
        </StyledTable>
      </LoadingContainer>
    </div>
  );
};

export default UserTable;
